import React from 'react';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { NoteType } from 'routes/screens/response/types';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
import { AccountInfoType, AllUsersType, UserType } from 'shared/types';
import { getAccountTimeFormat } from 'shared/utils';
import { NoteCategoryType, NoteTypeInfo } from '../../../../constants';
import { StyledNote } from '../../style';

type ResponseNoteType = {
  note: NoteType;
  allUsers: AllUsersType;
  setDeleteNoteId: (noteId: string) => void;
  toggleConfirmation: (dismiss: boolean) => void;
  accountInfo?: AccountInfoType;
  setEditNoteId: (noteId: string) => void;
  workflowPlaceholders: any;
  isFetchingWorkflow: boolean;
  userInfo: UserType;
};

const ResponseNote = (props: ResponseNoteType) => {
  const { t } = useTranslation();
  const {
    note,
    allUsers,
    accountInfo,
    setDeleteNoteId,
    toggleConfirmation,
    setEditNoteId,
    isFetchingWorkflow,
    workflowPlaceholders,
    userInfo,
  } = props;

  const getUserName = (userId: string) => {
    if (!userId) return 'System';
    const userName = `${get(allUsers, [userId, 'first_name'], '') || ''} ${
      get(allUsers, [userId, 'middle_name'], '') || ''
    } ${get(allUsers, [userId, 'last_name'], '') || ''}`.trim();
    const userEmail = get(allUsers, [userId, 'email'], '') || '';

    const createdBy = userName || userEmail || `${t('contacts.unknown_user')}`;
    return createdBy;
  };

  const userNote = !note.action;
  const isNoteHidden =
    NoteTypeInfo.find(({ action }) => action === note.action)?.isHidden ||
    false;
  const noteType =
    NoteTypeInfo.find(({ action }) => action === note.action)?.category ||
    'default';

  const handleDelete = () => {
    setDeleteNoteId(note.display_id);
    toggleConfirmation(true);
  };

  const sanitizeMessage = (message: any) => {
    if (message?.length && workflowPlaceholders) {
      const pattern = new RegExp(
        Object.keys(workflowPlaceholders).join('|'),
        'gi',
      );
      const match = message?.match(pattern)?.length
        ? message.match(pattern)[0]
        : null;
      if (match) {
        const { workflow_id, name, deleted } = workflowPlaceholders[match];
        if (deleted)
          return (
            <Trans
              i18nKey="responses.workflow_trigger"
              values={{ name: name }}
              components={{
                name: <span className="no-underline"></span>,
              }}
            />
          );
        return (
          <Trans
            i18nKey="responses.workflow_trigger"
            values={{ name: name }}
            components={{
              name: (
                <span
                  onClick={() =>
                    window.open(
                      `${location.origin}/app/workflow/edit/${workflow_id}`,
                      '_blank',
                    )
                  }
                ></span>
              ),
            }}
          />
        );
      } else {
        return <span className="no-underline">{message.replace(':', '')}</span>;
      }
    }
  };

  if (isNoteHidden) return null;

  const noteTextTranslation = NoteTypeInfo.find(
    (cat: NoteCategoryType) => note.action === cat.action,
  )?.text;

  return (
    <StyledNote type={noteType}>
      {!userNote ? (
        <div className="note-type-icon">
          {note.action === 'Workflow' || note.action === 'worfklow' ? (
            <Icon glyph="workflow" width={14} height={14} />
          ) : (
            <Icon glyph="info" width={14} height={14} />
          )}
        </div>
      ) : null}
      <div className="note-body">
        {note.action !== 'Workflow' && !userNote ? (
          <div className="label">
            {noteTextTranslation && `${t(noteTextTranslation)}`}
          </div>
        ) : null}

        {userNote ? (
          <div className="note-actions">
            <div onClick={() => setEditNoteId(note.display_id)}>
              <Icon glyph="edit" width="14px" height="14px" fill="none" />
            </div>
            <div style={{ color: '#BF4339' }} onClick={() => handleDelete()}>
              <Icon glyph="delete" width="14px" height="14px" fill="none" />
            </div>
          </div>
        ) : null}

        {(noteType !== 'default' && note.action !== 'Valid') || userNote ? (
          <div className="message">
            {note.action === 'Workflow' ? (
              <>
                {!isFetchingWorkflow ? (
                  sanitizeMessage(note?.notes)
                ) : (
                  <>
                    <Skeleton width={240} height={14} />
                  </>
                )}
              </>
            ) : (
              note?.notes
            )}
          </div>
        ) : (
          <Spacer y={0.3} />
        )}
        <div className="created-info">
          <div className="created-by">by {getUserName(note.created_by)}</div>
          {note?.created_at && (
            <div className="created-at">
              {moment(note.created_at)
                .tz(accountInfo?.timezone || moment.tz.guess())
                .format(getAccountTimeFormat(accountInfo))}
            </div>
          )}
        </div>
      </div>
    </StyledNote>
  );
};

export default ResponseNote;
